<template>
  <Modal
    :is-visible="!!report"
    :title="title"
    wide
    hide-cancel
    confirm-label="Close"
    @confirm="$emit('close')"
    @cancel="$emit('close')"
  >
    <div v-if="report" class="report-body">
      <p class="kicker">{{ subtitle }}</p>
      <p class="outcomes">{{ outcomeLine }}</p>
      <p class="when">{{ whenLine }}</p>
      <p v-if="report.kind === 'counter_intel'" class="ci-copy">
        {{ counterCopy }}
      </p>
      <IntelGrid
        v-if="showGrid"
        :formation="report.formation"
        :revealed="report.revealed"
        :race="report.other_race"
      />
      <p v-if="fogHint" class="fog-hint">
        {{ fogHint }}
      </p>
      <router-link
        v-if="mapTo"
        class="map-link"
        :to="mapTo"
        @click="$emit('close')"
      >
        View on map
      </router-link>
    </div>
  </Modal>
</template>

<script setup>
import { computed } from "vue";
import Modal from "./Modal.vue";
import IntelGrid from "./IntelGrid.vue";

const props = defineProps({
  report: {
    type: Object,
    default: null,
  },
});

defineEmits(["close"]);

function paperHasGridIntel(paper) {
  if (!paper || typeof paper.formation !== "object" || paper.formation == null) {
    return false;
  }
  return Array.isArray(paper.revealed) && paper.revealed.length > 0;
}

const showGrid = computed(
  () =>
    props.report?.kind === "scout_intel" ||
    (props.report?.kind === "counter_intel" && paperHasGridIntel(props.report))
);

const fogHint = computed(() => {
  if (!props.report) return "";
  if (props.report.kind === "scout_intel") {
    return "Gray squares remain hidden. Each successful scout cleared 4 tiles.";
  }
  if (props.report.kind === "counter_intel" && paperHasGridIntel(props.report)) {
    return "Gray squares remain hidden. Each captured scout revealed 4 tiles.";
  }
  return "";
});

const title = computed(() => {
  if (!props.report) return "Intel Report";
  if (props.report.kind === "counter_intel") return "Counter-Intelligence";
  return "World Intel";
});

const subtitle = computed(() => {
  const report = props.report;
  if (!report) return "";
  if (report.kind === "counter_intel") {
    return `Captured scouts from ${report.other_nickname || "an unknown empire"}`;
  }
  return report.city_name || report.other_nickname || "Enemy city";
});

const outcomeLine = computed(() => {
  const o = props.report?.outcomes || {};
  const success = Math.max(0, Math.floor(Number(o.success) || 0));
  const killed = Math.max(0, Math.floor(Number(o.killed) || 0));
  const captured = Math.max(0, Math.floor(Number(o.captured) || 0));
  const sent = Math.max(0, Math.floor(Number(o.sent) || 0));
  if (props.report?.kind === "counter_intel") {
    return captured === 1
      ? "1 scout was captured."
      : `${captured} scouts were captured.`;
  }
  return `${success} returned · ${killed} killed · ${captured} captured (${sent} sent)`;
});

const whenLine = computed(() => {
  if (!props.report?.created_at) return "";
  const date = new Date(props.report.created_at);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
});

const counterCopy = computed(() => {
  const report = props.report;
  if (!report) return "";
  const n = Math.max(0, Math.floor(Number(report.outcomes?.captured) || 0));
  const who = report.other_nickname || "An unknown empire";
  return `${who} sent scouts toward your city. ${n} ${
    n === 1 ? "was" : "were"
  } captured.`;
});

const mapTo = computed(() => {
  const id = props.report?.other_user_id;
  if (id == null) return null;
  return { path: "/game/world", query: { empire: String(id) } };
});
</script>

<style scoped>
.report-body {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.kicker {
  margin: 0;
  font-family: "Cinzel", serif;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #c9b48a;
  font-size: 0.85rem;
  text-align: center;
}

.outcomes,
.when,
.fog-hint,
.ci-copy {
  margin: 0;
  text-align: center;
}

.outcomes {
  color: #e7ecf1;
  font-weight: 600;
}

.when,
.fog-hint {
  font-size: 0.78rem;
  color: #8892b0;
}

.ci-copy {
  padding: 1rem;
  background: rgba(191, 97, 106, 0.12);
  border: 1px solid rgba(191, 97, 106, 0.35);
  border-radius: 6px;
  color: #e7ecf1;
}

.map-link {
  display: block;
  margin-top: 0.35rem;
  text-align: center;
  color: #00f0ff;
  text-decoration: none;
  font-size: 0.8rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.map-link:hover {
  text-decoration: underline;
}
</style>
